import { AssetField } from '@/components/builder/asset-field';
import {
  AngleDial,
  Disclosure,
  Popover,
  Section,
  Slider,
  ToggleGroup,
  TokenGrid,
  useScrub,
} from '@/components/builder/inspector-controls';
import {
  BoolInput,
  ConfigField,
  Field,
  NumberInput,
  PaddingField,
  RequiresField,
  SelectInput,
  SizeField,
  SpansField,
  TextInput,
  TokenPicker,
} from '@/components/builder/inspector-fields';
import { nodeIssues, type FieldIssues, type StepCtx } from '@/components/builder/validate';
import { cn } from '@/lib/cn';
import type { Action, BlockNode, Theme } from '@personalizehq/schemas/ir';
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignStartVertical,
  ArrowDown,
  ArrowRight,
  Blend,
  StretchHorizontal,
  StretchVertical,
  X,
} from 'lucide-react';
import { useState } from 'react';

// THE SECTION REGISTRY (B2).
//
// Plasmic needs `getOrderedSections()` plus ~20 `isTplX()` predicates because its element space is
// OPEN — anyone can register a component, so which panels apply has to be computed. Ours is a CLOSED
// 12-member union, so that whole layer collapses into one exhaustive table that the compiler checks.
//
// Section ORDER varies by block, and that is the point: an `image` leads with its source and its
// media controls, a `stack` leads with layout. Same panels, different first thing you see, which is
// most of what makes a panel feel considered.
//
// R1 — THE RULE THAT KEEPS US HONEST. A section is omitted only when the block TYPE HAS NO SUCH
// FIELD, never because the UI judges it meaningless. `style`, `actions`, `a11y_*`, `requires` and
// `animate` all live on `BlockBase`, so EVERY block gets style/motion/interaction/compat — including
// `spacer`, which the plan's own sketch wanted to cut down to "size, style, compat". A spacer may
// legally carry an action and a background; hiding those would invent a law the schema does not have.
// (Plasmic hides `background` on an image for taste. That is exactly the mistake.)
const ALIGN_X = [
  { value: 'start', icon: AlignStartVertical },
  { value: 'center', icon: AlignCenterVertical },
  { value: 'end', icon: AlignEndVertical },
  { value: 'stretch', icon: StretchVertical },
] as const;

const ALIGN_Y = [
  { value: 'start', icon: AlignStartHorizontal },
  { value: 'center', icon: AlignCenterHorizontal },
  { value: 'end', icon: AlignEndHorizontal },
  { value: 'stretch', icon: StretchHorizontal },
] as const;

// `space_between` has no alignment icon in any kit — it is a DISTRIBUTION, not an alignment — so it
// borrows the one that reads as "push apart".
const JUSTIFY = [
  { value: 'start', icon: AlignStartHorizontal },
  { value: 'center', icon: AlignCenterHorizontal },
  { value: 'end', icon: AlignEndHorizontal },
  { value: 'space_between', icon: Blend, title: 'space between' },
] as const;

type SectionId =
  | 'content'
  | 'layout'
  | 'media'
  | 'typography'
  | 'size'
  | 'style'
  | 'motion'
  | 'interaction'
  | 'compat';

/** Exhaustive by construction: a new block kind will not compile until it is given an order. */
const ORDER: Record<BlockNode['block'], readonly SectionId[]> = {
  stack: ['layout', 'size', 'style', 'motion', 'interaction', 'compat'],
  box: ['layout', 'size', 'style', 'motion', 'interaction', 'compat'],
  scroll: ['layout', 'size', 'style', 'motion', 'interaction', 'compat'],
  grid: ['layout', 'size', 'style', 'motion', 'interaction', 'compat'],
  text: ['content', 'typography', 'size', 'style', 'motion', 'interaction', 'compat'],
  button: ['content', 'typography', 'size', 'style', 'motion', 'interaction', 'compat'],
  image: ['content', 'media', 'size', 'style', 'motion', 'interaction', 'compat'],
  video: ['content', 'media', 'size', 'style', 'motion', 'interaction', 'compat'],
  progress: ['content', 'size', 'style', 'motion', 'interaction', 'compat'],
  repeat: ['content', 'size', 'style', 'motion', 'interaction', 'compat'],
  custom: ['content', 'size', 'style', 'motion', 'interaction', 'compat'],
  // No content, no layout, no type, no media — a spacer really is only a size. Everything else it
  // still gets, because `BlockBase` still gives it.
  spacer: ['size', 'style', 'motion', 'interaction', 'compat'],
};

const TITLE: Record<SectionId, string> = {
  content: 'Content',
  layout: 'Layout',
  media: 'Media',
  typography: 'Type',
  size: 'Size',
  style: 'Style',
  motion: 'Motion',
  interaction: 'Interaction',
  compat: 'Compatibility',
};

export function BlockSections({
  node,
  parent,
  root,
  step,
  theme,
  set,
  inScroll,
}: {
  node: BlockNode;
  parent: BlockNode | null;
  root: BlockNode | null;
  step?: StepCtx | undefined;
  theme: Theme;
  set: Setter;
  inScroll: boolean;
}): React.JSX.Element {
  const issues: FieldIssues = nodeIssues(node, parent, theme);
  const props = { node, parent, root, step, theme, set, issues, inScroll };

  return (
    <>
      {ORDER[node.block].map((id) => (
        <SectionBody key={id} id={id} {...props} />
      ))}
    </>
  );
}

const NONE_SCROLL = '—';

/** Every `scroll` in the step, with the number of cards it holds — which is exactly the number of
 *  dots that may track it (SPEC §7 / `checkProgressTracks`). */
function collectScrolls(root: BlockNode): { id: string; pages: number }[] {
  const found: { id: string; pages: number }[] = [];
  const walk = (n: BlockNode): void => {
    if (n.block === 'scroll') found.push({ id: n.id, pages: n.children.length });
    if ('children' in n) n.children.forEach(walk);
  };
  walk(root);
  return found;
}

interface SectionProps {
  node: BlockNode;
  parent: BlockNode | null;
  /** The step, for the ROOT's height law — it needs the surface, and only the document sees both. */
  step?: StepCtx | undefined;
  /** The step's ROOT. `progress.tracks` is a document-level relationship — it names a scroll that
   *  may be anywhere in the step — so it cannot be offered from the node alone. */
  root: BlockNode | null;
  theme: Theme;
  set: Setter;
  issues: FieldIssues;
  inScroll: boolean;
}

function SectionBody({ id, ...p }: { id: SectionId } & SectionProps): React.JSX.Element | null {
  const styleIssue = Object.keys(p.issues).some((k) => k.startsWith('style.'));

  switch (id) {
    case 'content':
      return (
        <Section title={TITLE.content}>
          <ContentSection {...p} />
        </Section>
      );
    case 'layout':
      return (
        <Section title={TITLE.layout}>
          <LayoutSection {...p} />
        </Section>
      );
    case 'media':
      return (
        <Section title={TITLE.media}>
          <MediaSection {...p} />
        </Section>
      );
    case 'typography':
      return (
        <Section title={TITLE.typography}>
          <TypographySection {...p} />
        </Section>
      );
    case 'size':
      return (
        <Section title={TITLE.size}>
          <SizeField
            node={p.node}
            parent={p.parent}
            step={p.step}
            size={p.node.size}
            onChange={(size, field) => p.set(field ?? 'size', (n) => (size ? (n.size = size) : delete n.size))}
            errors={{ width: p.issues['size.width'], height: p.issues['size.height'] }}
          />
        </Section>
      );
    case 'style':
      return (
        <Section title={TITLE.style} hasIssue={styleIssue}>
          <StyleSection {...p} />
        </Section>
      );
    case 'motion':
      return <MotionField node={p.node} set={p.set} issues={p.issues} inScroll={p.inScroll} />;
    case 'interaction':
      return <InteractionSection {...p} />;
    case 'compat':
      return <CompatSection {...p} />;
  }
}

type Setter = <T extends BlockNode>(field: string, mutate: (node: T) => void) => void;

/** Does this block name ITSELF? `text` and `button` derive their a11y label from their own spans
 *  (SPEC §5.0a), so a missing `a11y_label` there is fine — the nag is for everything else. */
const hasOwnText = (n: BlockNode): boolean => n.block === 'text' || n.block === 'button';
// ---------------------------------------------------------------------------- layout

function LayoutSection({ node, set, issues }: SectionProps): React.JSX.Element | null {
  switch (node.block) {
    case 'stack':
      return (
        <>
          <Field label="direction">
            <ToggleGroup
              label="direction"
              value={node.direction}
              options={[
                { value: 'column', icon: ArrowDown },
                { value: 'row', icon: ArrowRight },
              ]}
              onChange={(direction) => set('direction', (n) => void ((n as typeof node).direction = direction))}
            />
          </Field>
          <Scrub label="gap" value={node.gap} onChange={(gap) => set('gap', (n) => void ((n as typeof node).gap = gap))} />
          {/* ALIGN IS THE CROSS AXIS, JUSTIFY IS THE MAIN ONE — so which icons mean what FLIPS with
              `direction`. Showing vertical-alignment icons for `align` on a row would be the panel
              lying about the axis it edits. */}
          <Field label="align">
            <ToggleGroup
              label="align"
              value={node.align}
              options={node.direction === 'row' ? ALIGN_Y : ALIGN_X}
              onChange={(align) => set('align', (n) => void ((n as typeof node).align = align))}
            />
          </Field>
          <Field label="justify">
            <ToggleGroup
              label="justify"
              value={node.justify}
              options={JUSTIFY}
              onChange={(justify) => set('justify', (n) => void ((n as typeof node).justify = justify))}
            />
          </Field>
          <PaddingField padding={node.padding} onChange={(padding) => setPadding(set, padding)} />
        </>
      );

    case 'box':
      return (
        <>
          <Field label="align x">
            <ToggleGroup
              label="align x"
              value={node.align_x}
              options={ALIGN_X}
              onChange={(align_x) => set('align_x', (n) => void ((n as typeof node).align_x = align_x))}
            />
          </Field>
          <Field label="align y">
            <ToggleGroup
              label="align y"
              value={node.align_y}
              options={ALIGN_Y}
              onChange={(align_y) => set('align_y', (n) => void ((n as typeof node).align_y = align_y))}
            />
          </Field>
          <PaddingField padding={node.padding} onChange={(padding) => setPadding(set, padding)} />
        </>
      );

    case 'scroll':
      return (
        <>
          <Field label="axis">
            <ToggleGroup
              label="axis"
              value={node.axis}
              options={[
                { value: 'vertical', icon: ArrowDown },
                { value: 'horizontal', icon: ArrowRight },
              ]}
              onChange={(axis) => set('axis', (n) => void ((n as typeof node).axis = axis))}
            />
          </Field>
          <Field label="snap" set={node.snap === true}>
            <BoolInput
              label="snap to cards"
              value={node.snap}
              onChange={(snap) =>
                set('snap', (n) => {
                  const t = n as typeof node;
                  if (snap) t.snap = true;
                  else {
                    delete t.snap;
                    // THE SCHEMA REJECTS `auto_advance_ms` WITHOUT `snap` — there are no snap points
                    // to advance between. Clearing snap while a timing survives would author a
                    // document that cannot publish, so the timing goes with it.
                    delete t.auto_advance_ms;
                  }
                })
              }
            />
          </Field>
          {/* THE FIELD THAT MAKES A CAROUSEL A CAROUSEL, and it was unauthorable. It is offered only
              once `snap` is on, because the schema will not take one without the other (B3). */}
          {node.snap ? (
            <Field
              label="auto advance"
              error={issues['auto_advance_ms']}
              set={node.auto_advance_ms !== undefined}
              onUnset={() => set('auto_advance_ms', (n) => void delete (n as typeof node).auto_advance_ms)}
            >
              <NumberInput
                aria-label="auto advance"
                min={1000}
                max={60_000}
                step={500}
                value={node.auto_advance_ms}
                onChange={(v) =>
                  set('auto_advance_ms', (n) => {
                    const t = n as typeof node;
                    if (v === undefined) delete t.auto_advance_ms;
                    else t.auto_advance_ms = v;
                  })
                }
              />
              <span className="text-[11px] text-muted-foreground">ms</span>
            </Field>
          ) : null}
        </>
      );

    case 'grid':
      return (
        <>
          <Field label="cols" error={issues['cols']}>
            <NumberInput
              aria-label="cols"
              min={1}
              max={12}
              value={node.cols}
              onChange={(cols) => set('cols', (n) => void ((n as typeof node).cols = cols ?? 1))}
            />
          </Field>
          <Scrub label="h spacing" value={node.h_spacing} onChange={(v) => set('h_spacing', (n) => void ((n as typeof node).h_spacing = v))} />
          <Scrub label="v spacing" value={node.v_spacing} onChange={(v) => set('v_spacing', (n) => void ((n as typeof node).v_spacing = v))} />
          <PaddingField padding={node.padding} onChange={(padding) => setPadding(set, padding)} />
        </>
      );

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------- content

function ContentSection({ node, root, theme, set, issues }: SectionProps): React.JSX.Element | null {
  switch (node.block) {
    case 'text':
      return (
        <SpansField
          spans={node.spans}
          errors={issues}
          onChange={(spans) => set('spans', (n) => void ((n as typeof node).spans = spans))}
        />
      );

    case 'button':
      return (
        <SpansField
          spans={node.label}
          errors={issues}
          onChange={(label) => set('label', (n) => void ((n as typeof node).label = label))}
        />
      );

    case 'image':
    case 'video':
      return (
        <AssetField
          label="url"
          url={node.source.url}
          onChange={(url) => set('source', (n) => void ((n as typeof node).source = { kind: 'url', url }))}
        />
      );

    case 'custom':
      return (
        <>
          <Field label="identifier" error={issues['identifier']}>
            <TextInput aria-label="identifier" value={node.identifier} onChange={(identifier) => set('identifier', (n) => void ((n as typeof node).identifier = identifier))} />
          </Field>
          <ConfigField config={node.config} onChange={(config) => set('config', (n) => void ((n as typeof node).config = config))} />
        </>
      );

    case 'progress':
      return <ProgressContent node={node} root={root} theme={theme} set={set} issues={issues} />;

    case 'repeat': {
      // Both arms of `source` are a KEY into the SDK's items map (SPEC §7.2): `campaigns` reads the
      // SDK's own eligible-campaign list, `host` reads a list the app pushed with setItems(). One
      // field names the key either way — which is why they share an input.
      const kind = node.source.kind;
      const key = node.source.kind === 'host' ? node.source.key : node.source.collection;
      return (
        <>
          <Field label="source">
            <SelectInput
              aria-label="source"
              value={kind}
              options={['campaigns', 'host'] as const}
              onChange={(next) =>
                set('source', (n) => {
                  (n as typeof node).source =
                    next === 'host' ? { kind: 'host', key } : { kind: 'campaigns', collection: key };
                })
              }
            />
          </Field>
          <Field label={kind === 'host' ? 'key' : 'collection'} error={issues['source.key'] ?? issues['source.collection']}>
            <TextInput
              aria-label={kind === 'host' ? 'key' : 'collection'}
              value={key}
              onChange={(k) =>
                set('source', (n) => {
                  (n as typeof node).source =
                    kind === 'host' ? { kind: 'host', key: k } : { kind: 'campaigns', collection: k };
                })
              }
            />
          </Field>
          <Field label="max" error={issues['max']}>
            <NumberInput aria-label="max" min={1} max={50} value={node.max} onChange={(v) => set('max', (n) => void ((n as typeof node).max = v ?? 1))} />
          </Field>
          <p className="text-[11px] text-muted-foreground">
            {kind === 'host'
              ? 'The app pushes this list once with setItems(). Static items — no fetch, no refresh.'
              : 'Repeats over the SDK’s own eligible campaigns. Held locally; no network.'}{' '}
            The template is one block, repeated per item. Editing it on the canvas is a later slice.
          </p>
        </>
      );
    }

    default:
      return null;
  }
}

/** `progress` is its own component only because `tracks` needs the whole step to offer a choice, and
 *  threading the step through every section for one field would be worse than one prop here. */
function ProgressContent({
  node,
  root,
  theme,
  set,
  issues,
}: {
  node: Extract<BlockNode, { block: 'progress' }>;
  root: BlockNode | null;
  theme: Theme;
  set: Setter;
  issues: FieldIssues;
}): React.JSX.Element {
  const scrolls = root ? collectScrolls(root) : [];

  return (
    <>
      {/* THE DOTS THAT FOLLOW THE CARDS — and the other field that made a carousel a carousel while
          being unauthorable (B3).

          A PICKER OF THE SCROLLS IN THIS STEP, NEVER A FREE-TEXT FIELD. Publish already rejects a
          `tracks` that names something which is not a scroll, so an input that lets you type one is
          an editor that lies.

          And it MUST set `segments` too: the schema requires `segments === scroll.children.length`
          ("the dots would lie" — checkProgressTracks). Offering the id without the count would hand
          the author a document that cannot publish, from a control whose whole job is to connect the
          two. */}
      <Field
        label="tracks"
        error={issues['tracks']}
        set={node.tracks !== undefined}
        onUnset={() => set('tracks', (n) => void delete (n as typeof node).tracks)}
      >
        <SelectInput
          aria-label="tracks"
          value={node.tracks ?? NONE_SCROLL}
          options={[NONE_SCROLL, ...scrolls.map((s) => s.id)]}
          onChange={(id) =>
            set('tracks', (n) => {
              const t = n as typeof node;
              if (id === NONE_SCROLL) return void delete t.tracks;
              t.tracks = id;
              t.segments = scrolls.find((s) => s.id === id)?.pages ?? t.segments;
              if (t.value > t.segments) t.value = t.segments;
            })
          }
        />
      </Field>
      {scrolls.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">
          No scroll in this step to track. Add one, and its cards become these dots.
        </p>
      ) : null}

      <Field label="segments" error={issues['segments']}>
        <NumberInput
          aria-label="segments"
          min={1}
          max={32}
          // Owned by `tracks` when there is one: the count IS the scroll's card count, and letting
          // the author drift it apart only authors the lie the schema is there to prevent.
          disabled={node.tracks !== undefined}
          value={node.segments}
          onChange={(v) => set('segments', (n) => void ((n as typeof node).segments = v ?? 1))}
        />
      </Field>
      <Field label="value" error={issues['value']}>
        <NumberInput aria-label="value" min={0} value={node.value} onChange={(v) => set('value', (n) => void ((n as typeof node).value = v ?? 0))} />
      </Field>
      <Field label="gap">
        <NumberInput aria-label="gap" min={0} value={node.gap} onChange={(v) => set('gap', (n) => void ((n as typeof node).gap = v ?? 0))} />
      </Field>
      <Field label="fill color" error={issues['fill_color']}>
        <TokenPicker label="fill color" kind="colors" theme={theme} value={node.fill_color} onChange={(c) => c && set('fill_color', (n) => void ((n as typeof node).fill_color = c))} />
      </Field>
      <Field label="track color" error={issues['track_color']}>
        <TokenPicker label="track color" kind="colors" theme={theme} value={node.track_color} onChange={(c) => c && set('track_color', (n) => void ((n as typeof node).track_color = c))} />
      </Field>
    </>
  );
}

// ---------------------------------------------------------------------------- media

function MediaSection({ node, theme, set, issues }: SectionProps): React.JSX.Element | null {
  if (node.block === 'image') {
    return (
      <>
        {/* REQUIRED, and no `hug` width: intrinsic sizing would make layout depend on the image
            bytes, so the box would jump on decode.

            IT IS INERT WHEN THE HEIGHT IS `fill`. Then the box is the parent's remainder, the ratio
            decides nothing, and `fit` crops into whatever shape that turns out to be. The schema
            still REQUIRES a value, so the field cannot be removed — but a field that looks live and
            is not is the whole family of bug this slice exists to kill, so it says so. */}
        <Field label="aspect ratio" error={issues['aspect_ratio']}>
          <NumberInput
            aria-label="aspect ratio"
            step={0.01}
            min={0.01}
            disabled={node.size?.height === 'fill'}
            value={node.aspect_ratio}
            onChange={(v) => set('aspect_ratio', (n) => void ((n as typeof node).aspect_ratio = v ?? 1))}
          />
        </Field>
        {node.size?.height === 'fill' ? (
          <p className="px-1 text-[11px] text-muted-foreground">
            Ignored while the height is <code>fill</code> — the box is the leftover space, so the
            ratio decides nothing.
          </p>
        ) : null}
        {/* THE WORD "fill" MEANS THREE THINGS IN THIS PANEL, and one of them is here. A size `fill`
            takes the leftover space; `fit: "fill"` STRETCHES the pixels. The wire value cannot
            change (it is in a pinned binary), so the LABEL does the work. */}
        <Field label="fit">
          <SelectInput
            aria-label="fit"
            value={node.fit}
            options={['cover', 'contain', 'fill'] as const}
            labels={{ cover: 'cover — crop to fill', contain: 'contain — letterbox', fill: 'stretch — distort' }}
            onChange={(fit) => set('fit', (n) => void ((n as typeof node).fit = fit))}
          />
        </Field>
        {/* "image radius", not "radius". `image.radius` (this) and `style.radius` (the Style section)
            are different fields, and BOTH rendered as a row labelled "radius" in the same panel. */}
        <Field label="image radius" set={node.radius !== undefined}>
          <NumberInput aria-label="image radius" min={0} value={node.radius} onChange={(radius) => set('radius', (n) => void ((n as typeof node).radius = radius))} />
        </Field>
        <Field label="tint" error={issues['tint']}>
          <TokenPicker label="tint" kind="colors" theme={theme} optional value={node.tint} onChange={(tint) => set('tint', (n) => { if (tint) (n as typeof node).tint = tint; else delete (n as typeof node).tint; })} />
        </Field>
        {/* A cover crop keeps the CENTRE, which is exactly where faces are not (SPEC §5.0d).
            Only offered at fit:"cover" — nothing is cropped otherwise, and the schema rejects it. */}
        {node.fit === 'cover' ? (
          <Field label="focal" error={issues['focal']} set={node.focal !== undefined}>
            <NumberInput
              className="w-[56px]"
              aria-label="focal x"
              min={0}
              max={1}
              step={0.05}
              value={node.focal?.x}
              onChange={(x) =>
                set('focal', (n) => {
                  const t = n as typeof node;
                  if (x === undefined) delete t.focal;
                  else t.focal = { x, y: t.focal?.y ?? 0.5 };
                })
              }
            />
            <NumberInput
              className="w-[56px]"
              aria-label="focal y"
              min={0}
              max={1}
              step={0.05}
              value={node.focal?.y}
              onChange={(y) =>
                set('focal', (n) => {
                  const t = n as typeof node;
                  if (y === undefined) delete t.focal;
                  else t.focal = { x: t.focal?.x ?? 0.5, y };
                })
              }
            />
          </Field>
        ) : null}
      </>
    );
  }

  if (node.block === 'video') {
    return (
      <>
        <Field label="aspect ratio" error={issues['aspect_ratio']}>
          <NumberInput aria-label="aspect ratio" step={0.01} min={0.01} value={node.aspect_ratio} onChange={(v) => set('aspect_ratio', (n) => void ((n as typeof node).aspect_ratio = v ?? 1))} />
        </Field>
        <Field label="poster">
          <TextInput aria-label="poster" value={node.poster ?? ''} onChange={(poster) => set('poster', (n) => void ((n as typeof node).poster = poster || undefined))} />
        </Field>
        <Field label="playback">
          <span className="flex gap-2">
            <BoolInput label="autoplay" value={node.autoplay} onChange={(v) => set('autoplay', (n) => void ((n as typeof node).autoplay = v))} />
            <BoolInput label="muted" value={node.muted} onChange={(v) => set('muted', (n) => void ((n as typeof node).muted = v))} />
            <BoolInput label="loop" value={node.loop} onChange={(v) => set('loop', (n) => void ((n as typeof node).loop = v))} />
          </span>
        </Field>
        <Field label="video radius" set={node.radius !== undefined}>
          <NumberInput aria-label="video radius" min={0} value={node.radius} onChange={(radius) => set('radius', (n) => void ((n as typeof node).radius = radius))} />
        </Field>
      </>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------- typography

function TypographySection({ node, theme, set, issues }: SectionProps): React.JSX.Element | null {
  if (node.block === 'text') {
    return (
      <>
        <Field label="font" error={issues['font']}>
          <TokenPicker label="font" kind="fonts" theme={theme} value={node.font} onChange={(font) => font && set('font', (n) => void ((n as typeof node).font = font))} />
        </Field>
        <Field label="color" error={issues['color']}>
          <TokenPicker label="color" kind="colors" theme={theme} value={node.color} onChange={(color) => color && set('color', (n) => void ((n as typeof node).color = color))} />
        </Field>
        <Field label="align">
          <SelectInput
            aria-label="text align"
            value={node.align ?? 'start'}
            options={['start', 'center', 'end'] as const}
            onChange={(align) => set('align', (n) => void ((n as typeof node).align = align))}
          />
        </Field>
        {/* Empty = uncapped. The author sees "Alex" at design time and ships a card that breaks
            for "Bartholomew-Constantine" — this is the field that stops that (SPEC §6.11). */}
        <Field label="max lines" error={issues['max_lines']} set={node.max_lines !== undefined}>
          <NumberInput
            aria-label="max lines"
            min={1}
            value={node.max_lines}
            onChange={(v) => set('max_lines', (n) => void ((n as typeof node).max_lines = v))}
          />
        </Field>
      </>
    );
  }

  if (node.block === 'button') {
    return (
      <>
        <Field label="variant" error={issues['variant']}>
          <TokenPicker label="variant" kind="buttons" theme={theme} value={node.variant} onChange={(variant) => variant && set('variant', (n) => void ((n as typeof node).variant = variant))} />
        </Field>
        <Field label="font" error={issues['font']}>
          <TokenPicker label="font" kind="fonts" theme={theme} value={node.font} onChange={(font) => font && set('font', (n) => void ((n as typeof node).font = font))} />
        </Field>
      </>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------- style

function StyleSection({ node, theme, set, issues }: SectionProps): React.JSX.Element {
  return (
    <>
      <BackgroundField node={node} theme={theme} set={set} issues={issues} />
      <RadiusField node={node} set={set} />
      {/* PROGRESSIVE DISCLOSURE. Background and radius are what people reach for; opacity, border
          and shadow are not, and rendering all five as identical rows is how the panel got to ~25. */}
      <Disclosure>
        <Field
          label="opacity"
          set={node.style?.opacity !== undefined}
          onUnset={() => set('style.opacity', (n) => void assignStyle(n, { opacity: undefined }))}
        >
          {/* BOUNDED, so a slider is genuinely right — and it hands the walker `role="slider"` with
              the schema's own min/max, which a number box could only promise. */}
          <Slider
            label="opacity"
            value={node.style?.opacity}
            fallback={1}
            min={0}
            max={1}
            step={0.05}
            onChange={(opacity) => set('style.opacity', (n) => void assignStyle(n, { opacity }))}
          />
        </Field>

        <Field
          label="border"
          error={issues['style.border.color']}
          set={node.style?.border !== undefined}
          onUnset={() => set('style.border', (n) => void assignStyle(n, { border: undefined }))}
        >
          <BorderEditor node={node} theme={theme} set={set} />
        </Field>

        <Field
          label="shadow"
          error={issues['style.shadow.color']}
          set={node.style?.shadow !== undefined}
          onUnset={() => set('style.shadow', (n) => void assignStyle(n, { shadow: undefined }))}
        >
          <ShadowEditor node={node} theme={theme} set={set} />
        </Field>
      </Disclosure>
    </>
  );
}

// ---------------------------------------------------------------------------- interaction

function InteractionSection({ node, set, issues }: SectionProps): React.JSX.Element {
  return (
    // ANY block is tappable (SPEC §5.0), so this section runs for all 12 — it used to sit inside the
    // `button` arm, which is exactly why nothing else could be given an action.
    <Section title={TITLE.interaction}>
      <ActionsField
        actions={node.actions ?? []}
        issues={issues}
        onChange={(actions) => set('actions', (n) => void (n.actions = actions.length ? actions : undefined))}
      />
      {/* A11Y SITS IN THE SAME SECTION AS ACTIONS, ON PURPOSE. Put it in its own section — or behind
          a disclosure pill — and naming a tappable block becomes optional; here it is unavoidable,
          because you cannot make something tappable without seeing the field that names it (§5.0a).
          The B2 registry does NOT get to "tidy" this into its own section. */}
      <Field label="a11y label" error={issues['a11y_label']} set={node.a11y_label !== undefined}>
        <TextInput
          aria-label="a11y label"
          value={node.a11y_label ?? ''}
          placeholder={node.actions?.length ? 'what a screen reader says' : 'derived from content'}
          onChange={(v) => set('a11y_label', (n) => void (n.a11y_label = v || undefined))}
        />
      </Field>
      <Field label="decorative" set={node.a11y_hidden === true}>
        <BoolInput
          label="hide from screen readers"
          value={node.a11y_hidden ?? false}
          onChange={(v) => set('a11y_hidden', (n) => void (n.a11y_hidden = v || undefined))}
        />
      </Field>
      {/* A NAG, NOT A BLOCK. Refusing to save a draft over a missing label is hostile; shipping an
          unlabelled tap target is worse. So: warn, loudly, and let them publish. */}
      {node.actions?.length && !node.a11y_label && !hasOwnText(node) ? (
        <p className="px-1 text-[11px] text-amber-500">
          Tappable with no label — a screen reader will announce an unnamed button.
        </p>
      ) : null}
    </Section>
  );
}

// ---------------------------------------------------------------------------- compatibility

function CompatSection({ node, set }: SectionProps): React.JSX.Element {
  return (
    <Section title={TITLE.compat}>
      {/* A capability that a STYLE FIELD implies cannot be hand-removed. `backdrop` without
          `backdrop_blur` is a card that renders with NO BACKGROUND over the host app (SPEC §5.0e) —
          and the free-text editor would happily let you type it away. The gate belongs to the field;
          the author owns everything else in this list. */}
      <Disclosure>
        <RequiresField
          requires={node.requires}
          set={(node.requires?.length ?? 0) > 0}
          onChange={(requires) =>
            set('requires', (n) => {
              const next = new Set(requires ?? []);
              if (n.style?.backdrop) next.add('backdrop_blur');
              if (next.size > 0) n.requires = [...next];
              else delete n.requires;
            })
          }
        />
      </Disclosure>
      {/* An unregistered `custom` identifier, or an unsatisfied `requires`, renders the block's
          fallback — and NOTHING at all if it has none. */}
      {(node.requires?.length || node.block === 'custom') && !node.fallback ? (
        <p className="text-[11px] text-amber-500">
          No fallback: on a device that cannot render this block, nothing appears here.
        </p>
      ) : null}
    </Section>
  );
}
const ACTION_TYPES = [
  'dismiss',
  'open_url',
  'deep_link',
  'track_event',
  'flow_next',
  'flow_back',
  'launch_experience',
  'host_callback',
] as const;

/** Data-only, tagged on `type` — no code, no expressions, ever. `flow_next`/`flow_back` are how a
 *  button walks the steps; the WIRE flow model that honors them lands in P4. */
function ActionsField({
  actions,
  issues,
  onChange,
}: {
  actions: Action[];
  issues: FieldIssues;
  onChange: (actions: Action[]) => void;
}): React.JSX.Element {
  const replace = (index: number, action: Action): void =>
    onChange(actions.map((a, i) => (i === index ? action : a)));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">actions</span>
        <button
          type="button"
          className="rounded border px-1.5 py-0.5 text-[11px] hover:bg-secondary disabled:opacity-30"
          disabled={actions.length >= 4}
          onClick={() => onChange([...actions, { type: 'dismiss' }])}
        >
          + action
        </button>
      </div>

      {actions.map((action, index) => (
        <div key={index} className="space-y-1 rounded-md border p-1.5">
          <div className="flex items-center gap-1">
            <SelectInput
              className="flex-1"
              aria-label={`action ${index} type`}
              value={action.type}
              options={ACTION_TYPES}
              onChange={(type) => replace(index, blankAction(type))}
            />
            <button
              type="button"
              aria-label={`Remove action ${index}`}
              className="p-1 text-muted-foreground hover:text-destructive"
              onClick={() => onChange(actions.filter((_, i) => i !== index))}
            >
              <X className="size-3" />
            </button>
          </div>

          {action.type === 'open_url' || action.type === 'deep_link' ? (
            <Field label="url" error={issues[`actions.${index}.url`]}>
              <TextInput
                aria-label={`action ${index} url`}
                value={action.url}
                onChange={(url) => replace(index, { ...action, url })}
              />
            </Field>
          ) : null}
          {action.type === 'track_event' ? (
            <Field label="event" error={issues[`actions.${index}.name`]}>
              <TextInput
                aria-label={`action ${index} event`}
                value={action.name}
                onChange={(name) => replace(index, { ...action, name })}
              />
            </Field>
          ) : null}
          {action.type === 'launch_experience' ? (
            // No sane default exists — a placeholder campaign id would publish and silently launch
            // nothing. Ship the empty field and let the law show up under it.
            <Field label="campaign" error={issues[`actions.${index}.campaign_id`]}>
              <TextInput
                aria-label={`action ${index} campaign`}
                value={action.campaign_id}
                onChange={(campaign_id) => replace(index, { ...action, campaign_id })}
              />
            </Field>
          ) : null}
          {action.type === 'host_callback' ? (
            // The escape hatch: the host app registered a delegate under this name, so a button can
            // do anything the customer's app can do — with no SDK release and no schema change.
            <Field label="handler" error={issues[`actions.${index}.name`]}>
              <TextInput
                aria-label={`action ${index} handler`}
                value={action.name}
                onChange={(name) => replace(index, { ...action, name })}
              />
            </Field>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Placeholders where one can be honest (a URL, an event name — both are strings the author will
 *  overwrite, and both are publishable as-is). NOT for `launch_experience`: an invented campaign id
 *  would pass the schema and then launch nothing on device. That one starts empty and shows its law. */
function blankAction(type: (typeof ACTION_TYPES)[number]): Action {
  switch (type) {
    case 'open_url':
      return { type, url: 'https://example.com' };
    case 'deep_link':
      return { type, url: 'myapp://home' };
    case 'track_event':
      return { type, name: 'button_tapped' };
    // The name is the CUSTOMER's — whatever their app registered a delegate under. A placeholder is
    // honest here in a way an invented campaign id is not: an unregistered name is a documented
    // no-op (SPEC §9), not a silent failure.
    case 'host_callback':
      return { type, name: 'open_paywall' };
    case 'launch_experience':
      return { type, campaign_id: '' };
    default:
      return { type };
  }
}

/** Style is one optional object; a `radius` set on a block with no style must not throw. An
 *  all-undefined style is deleted, so nothing empty ends up on the wire. */
/** ENTRY / STAGGER / PARALLAX, plus the one gated style field (SPEC §5.0e).
 *
 *  `backdrop` lives here rather than under Style because it is the same question — how does this
 *  card FEEL — and because it is the only style field that drags a capability gate behind it. The
 *  gate is not optional: the inspector wires `requires[]` for you, because a `backdrop` without it
 *  is a card that renders with no background at all on every device that cannot blur. */
function MotionField({
  node,
  set,
  issues,
  inScroll,
}: {
  node: BlockNode;
  set: Setter;
  issues: FieldIssues;
  inScroll: boolean;
}): React.JSX.Element {
  const a = node.style?.animate;
  const entry = a?.entry;
  const isContainer = 'children' in node;

  /** THE GATE AND THE FALLBACK BOTH TRAVEL WITH THE FIELD.
   *
   *  An author cannot be expected to know that `backdrop` needs a capability string AND a fallback
   *  subtree — and a document missing either does not publish (SPEC §5.0e). So the inspector writes
   *  the degradation: the fallback IS this card, minus the blur, with its ids suffixed. That is
   *  exactly what the spec asks for ("the same card with a solid bg"), and it means the author gets
   *  a correct document by moving one slider. */
  const setBackdrop = (blur: number | undefined): void =>
    set('style.backdrop', (n) => {
      const req = new Set(n.requires ?? []);
      if (blur === undefined) {
        assignStyle(n, { backdrop: undefined });
        req.delete('backdrop_blur');
        // Only OUR fallback is withdrawn — one the author wrote themselves is theirs to keep.
        if (n.fallback?.id === `${n.id}__solid`) delete n.fallback;
      } else {
        assignStyle(n, { backdrop: { blur } });
        req.add('backdrop_blur');
        if (!n.fallback) n.fallback = solidFallback(n);
      }
      n.requires = req.size > 0 ? [...req] : undefined;
    });

  // MOTION IS ENTIRELY OPTIONAL — every row here is absent on the overwhelming majority of blocks,
  // which makes it the textbook case for disclosure rather than four permanent rows.
  return (
    <Section title="Motion" hasIssue={Object.keys(issues).some((k) => k.startsWith('style.'))}>
      <Disclosure>
      <Field label="entry" set={entry !== undefined}>
        <SelectInput
          className="w-[88px]"
          aria-label="entry"
          value={entry?.kind ?? 'none'}
          options={['none', 'fade', 'fade_up', 'scale_in'] as const}
          onChange={(kind) =>
            set('style.animate', (n) =>
              assignStyle(n, {
                animate: cleanAnimate({
                  ...a,
                  entry: kind === 'none' ? undefined : { kind, duration_ms: entry?.duration_ms ?? 400, ...(entry?.delay_ms !== undefined ? { delay_ms: entry.delay_ms } : {}) },
                }),
              })
            )
          }
        />
      </Field>
      {entry ? (
        <Field label="duration / delay">
          <span className="flex items-center gap-1">
            <NumberInput
              className="w-[56px]"
              aria-label="entry duration"
              min={50}
              max={5000}
              value={entry.duration_ms}
              onChange={(v) =>
                set('style.animate', (n) =>
                  assignStyle(n, { animate: cleanAnimate({ ...a, entry: { ...entry, duration_ms: v ?? 400 } }) })
                )
              }
            />
            <NumberInput
              className="w-[56px]"
              aria-label="entry delay"
              min={0}
              max={5000}
              value={entry.delay_ms}
              onChange={(v) =>
                set('style.animate', (n) =>
                  assignStyle(n, { animate: cleanAnimate({ ...a, entry: { ...entry, delay_ms: v } }) })
                )
              }
            />
          </span>
        </Field>
      ) : null}

      {/* STAGGER BELONGS TO THE CONTAINER, so it is only offered on one — a strip that cascades is
          ONE field on the strip, and it stays right when a card is added. Publish rejects it
          anywhere else. */}
      {isContainer ? (
        <Field
          label="stagger"
          error={issues['style.animate.stagger_ms']}
          set={a?.stagger_ms !== undefined}
          onUnset={() =>
            set('style.animate', (n) => assignStyle(n, { animate: cleanAnimate({ ...a, stagger_ms: undefined }) }))
          }
        >
          <NumberInput
            aria-label="stagger"
            min={0}
            max={1000}
            value={a?.stagger_ms}
            onChange={(v) =>
              set('style.animate', (n) =>
                assignStyle(n, { animate: cleanAnimate({ ...a, stagger_ms: v }) })
              )
            }
          />
        </Field>
      ) : null}

      {/* PARALLAX IS SCROLL-DRIVEN, so it is only offered when there IS a scroll above: it
          translates against that scroll's offset, and without one it would never move. Publish
          rejects it — an inspector that offers it anyway is an inspector that lies. */}
      {inScroll ? (
        <Field
          label="parallax"
          error={issues['style.animate.parallax']}
          set={a?.parallax !== undefined}
          onUnset={() =>
            set('style.animate', (n) => assignStyle(n, { animate: cleanAnimate({ ...a, parallax: undefined }) }))
          }
        >
          {/* Centre-detented at 0 by construction: −1…1 with 0 in the middle, so "no parallax" is
              the resting position of the control rather than a number you have to know to type. */}
          <Slider
            label="parallax"
            value={a?.parallax}
            fallback={0}
            min={-1}
            max={1}
            step={0.05}
            onChange={(v) =>
              set('style.animate', (n) =>
                assignStyle(n, { animate: cleanAnimate({ ...a, parallax: v }) })
              )
            }
          />
        </Field>
      ) : null}

      <Field
        label="backdrop blur"
        error={issues['style.backdrop']}
        set={node.style?.backdrop !== undefined}
        onUnset={() => setBackdrop(undefined)}
      >
        <NumberInput aria-label="backdrop blur" min={0} max={100} value={node.style?.backdrop?.blur} onChange={setBackdrop} />
      </Field>
      </Disclosure>
      {node.style?.backdrop ? (
        <p className="px-1 text-[11px] text-muted-foreground">
          Gated on <code>backdrop_blur</code> — needs a fallback, or the card vanishes on a device
          that cannot blur.
        </p>
      ) : null}
    </Section>
  );
}


/** THE DEGRADATION, WRITTEN FOR YOU: this card without the blur. A gated block with no fallback is
 *  DROPPED when the capability is missing (§7.1) — the card would simply vanish on a device that
 *  cannot blur, which is strictly worse than the flat version of it.
 *
 *  Ids are suffixed because they must stay unique within the document: the snapshot is keyed by id,
 *  and a duplicate would make two nodes fight over one rect. */
function solidFallback(node: BlockNode): BlockNode {
  // A JSON round-trip, NOT structuredClone: `node` is an immer DRAFT here, and a Proxy cannot be
  // structured-cloned (DATA_CLONE_ERR). The IR is plain JSON by construction — that is the whole
  // point of a no-code, no-eval wire format — so this is both safe and exact.
  const clone = JSON.parse(JSON.stringify(node)) as BlockNode;
  const rename = (n: BlockNode): void => {
    n.id = `${n.id}__solid`;
    if ('children' in n) n.children.forEach(rename);
  };
  rename(clone);
  delete clone.requires;
  delete clone.fallback;
  if (clone.style) {
    const style = { ...clone.style };
    delete style.backdrop;
    clone.style = style;
  }
  // The id the setter looks for when the author turns the blur back off.
  clone.id = `${node.id}__solid`;
  return clone;
}

/** An `animate` with every member cleared is not an empty object on the wire — it is ABSENT. */
function cleanAnimate(a: NonNullable<BlockNode['style']>['animate']): NonNullable<BlockNode['style']>['animate'] {
  if (!a) return undefined;
  const next = { ...a };
  for (const [k, v] of Object.entries(next)) {
    if (v === undefined) delete (next as Record<string, unknown>)[k];
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/** `bg` is a colour token OR a linear gradient (SPEC §5.0c). Gradient STOPS are tokens too — that
 *  is what keeps dark mode alive on the biggest colour surface in the card, so they are token
 *  pickers, not colour wells. */
function BackgroundField({
  node,
  theme,
  set,
  issues,
}: {
  node: BlockNode;
  theme: Theme;
  set: Setter;
  issues: FieldIssues;
}): React.JSX.Element {
  const bg = node.style?.bg;
  const gradient = typeof bg === 'object' ? bg : undefined;

  // The live strip. `stops` are TOKENS and evenly distributed (SPEC §5.0c — there are no per-stop
  // offsets), so the preview is exactly `linear-gradient(angle, ...light halves)`. Seeing the thing
  // is the entire point: an angle and a list of token names is not a gradient, it is a puzzle.
  const strip = gradient
    ? `linear-gradient(${gradient.angle}deg, ${gradient.stops
        .map((s) => theme.colors[s]?.light ?? 'transparent')
        .join(', ')})`
    : undefined;

  return (
    <>
      <Field
        label="background"
        error={issues['style.bg']}
        set={bg !== undefined}
        onUnset={() => set('style.bg', (n) => void assignStyle(n, { bg: undefined }))}
      >
        <SelectInput
          className="w-[80px]"
          aria-label="background kind"
          value={gradient ? 'gradient' : 'solid'}
          options={['solid', 'gradient'] as const}
          onChange={(kind) =>
            set('style.bg', (n) => {
              if (kind === 'solid') {
                assignStyle(n, { bg: gradient ? gradient.stops[0] : undefined });
              } else {
                const seed = typeof bg === 'string' ? bg : firstKey(theme.colors);
                assignStyle(n, { bg: { kind: 'linear', angle: 180, stops: [seed, seed] } });
              }
            })
          }
        />
      </Field>

      {gradient ? (
        <>
          <div className="h-6 rounded-md border" style={{ background: strip }} />
          <Field label="angle">
            <AngleDial
              label="angle"
              value={gradient.angle}
              onChange={(angle) => set('style.bg', (n) => assignStyle(n, { bg: { ...gradient, angle } }))}
            />
          </Field>
          {gradient.stops.map((stop, i) => (
            <Field key={i} label={`stop ${i + 1}`} error={issues[`style.bg.stops.${i}`]}>
              <span className="flex items-center gap-1">
                <TokenPicker
                  label={`stop ${i + 1}`}
                  kind="colors"
                  theme={theme}
                  value={stop}
                  onChange={(c) =>
                    c &&
                    set('style.bg', (n) => {
                      const stops = [...gradient.stops];
                      stops[i] = c;
                      assignStyle(n, { bg: { ...gradient, stops } });
                    })
                  }
                />
                {/* The schema floor is 2 stops — a one-stop gradient is a solid colour with extra
                    steps, and the author has a Solid option for that. */}
                {gradient.stops.length > 2 ? (
                  <button
                    type="button"
                    aria-label={`Remove stop ${i + 1}`}
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      set('style.bg', (n) =>
                        assignStyle(n, {
                          bg: { ...gradient, stops: gradient.stops.filter((_, j) => j !== i) },
                        })
                      )
                    }
                  >
                    <X className="size-3" />
                  </button>
                ) : null}
              </span>
            </Field>
          ))}
          {gradient.stops.length < 8 ? (
            <button
              type="button"
              className="mx-1 text-left text-[11px] text-muted-foreground hover:text-foreground"
              onClick={() =>
                set('style.bg', (n) =>
                  assignStyle(n, {
                    bg: { ...gradient, stops: [...gradient.stops, gradient.stops[gradient.stops.length - 1]!] },
                  })
                )
              }
            >
              + stop
            </button>
          ) : null}
        </>
      ) : (
        <Field label="color" error={issues['style.bg']}>
          <TokenGrid
            label="background color"
            kind="colors"
            theme={theme}
            optional
            value={typeof bg === 'string' ? bg : undefined}
            onChange={(c) => set('style.bg', (n) => void assignStyle(n, { bg: c }))}
          />
        </Field>
      )}
    </>
  );
}

/** One radius, or four (SPEC §5.0c). The corners are PHYSICAL — a rounded card does not mirror in
 *  RTL — which is why they are labelled by position and not by `start`/`end`. */
const CORNERS = ['tl', 'tr', 'br', 'bl'] as const;

/** Plasmic's corner picker, and it maps one-to-one onto A7's `Corners {tl,tr,br,bl}`: SELECT the
 *  corners you mean, then type ONE number that edits exactly those. An empty selection means all
 *  four — which is also how the uniform `radius: number` form is authored, so the two shapes of the
 *  field stop being a mode the author has to choose and become a consequence of what they selected.
 *
 *  Four loose number boxes (what I shipped in A7) made you do that arithmetic yourself. */
type Corner = (typeof CORNERS)[number];

function RadiusField({ node, set }: { node: BlockNode; set: Setter }): React.JSX.Element {
  const r = node.style?.radius;
  const corners = typeof r === 'object' ? r : undefined;
  const [picked, setPicked] = useState<readonly Corner[]>([]);

  const valueAt = (k: Corner): number | undefined =>
    corners ? corners[k] : typeof r === 'number' ? r : undefined;

  // What the single number box shows: the selected corners' value when they agree, else blank —
  // the same "mixed" convention every design tool uses for a multi-selection.
  const values = (picked.length ? picked : CORNERS).map(valueAt);
  const shown = values.every((v) => v === values[0]) ? values[0] : undefined;

  const write = (value: number | undefined): void =>
    set('style.radius', (n) => {
      // Nothing selected → the UNIFORM form. Fewer bytes on the wire, and it is what the author
      // means when they say "round the corners".
      if (!picked.length) return void assignStyle(n, { radius: value });

      const next = { tl: valueAt('tl') ?? 0, tr: valueAt('tr') ?? 0, br: valueAt('br') ?? 0, bl: valueAt('bl') ?? 0 };
      for (const k of picked) next[k] = value ?? 0;
      // Four equal corners ARE a uniform radius. Collapsing keeps one shape of the field on the
      // wire per meaning, rather than two that render identically.
      assignStyle(n, { radius: CORNERS.every((k) => next[k] === next.tl) ? next.tl : next });
    });

  return (
    <Field
      label="radius"
      set={r !== undefined}
      onUnset={() => set('style.radius', (n) => void assignStyle(n, { radius: undefined }))}
    >
      <div role="group" aria-label="corners" className="grid size-7 shrink-0 grid-cols-2 gap-px">
        {CORNERS.map((k) => {
          const on = picked.includes(k);
          return (
            <button
              key={k}
              type="button"
              role="checkbox"
              aria-checked={on}
              aria-label={`corner ${k}`}
              title={k}
              onClick={() => setPicked(on ? picked.filter((p) => p !== k) : [...picked, k])}
              className={cn(
                'rounded-[2px] border',
                // The button sits where its corner does, so the control looks like the box it rounds.
                k === 'tl' && 'rounded-tl-md',
                k === 'tr' && 'rounded-tr-md',
                k === 'br' && 'rounded-br-md',
                k === 'bl' && 'rounded-bl-md',
                on ? 'border-indigo-500 bg-indigo-500/20' : 'border-border'
              )}
            />
          );
        })}
      </div>
      <NumberInput
        className="w-[56px]"
        aria-label="radius"
        min={0}
        value={shown}
        onChange={write}
      />
    </Field>
  );
}

/** An UNBOUNDED extent (gap, spacing): drag the label, or type in the box. A slider needs two ends
 *  and `gap` has only one, which is why these scrub and `opacity` does not. The `<input
 *  type="number">` underneath is untouched by the drag — so the role-walker still sees a spinbutton
 *  carrying the schema's `min`. */
function Scrub({
  label,
  value,
  onChange,
  min = 0,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
}): React.JSX.Element {
  const scrub = useScrub({ value, onChange, min });
  return (
    <Field label={label} scrub={scrub} set={value !== 0}>
      <NumberInput aria-label={label} min={min} value={value} onChange={(n) => onChange(n ?? 0)} />
    </Field>
  );
}

/** One popover, and the trigger IS the preview — Plasmic's `shadow-thumb`. Five loose number boxes
 *  in a row told you the numbers; this tells you the shadow.
 *
 *  THE ANCHOR RULE SURVIVES THE MOVE. `y` is the member that CREATES the shadow, so clearing it
 *  clears the whole object — the schema has no notion of a shadow without one. `x` and `spread`
 *  default to absent, so a v1.0 document keeps painting exactly as it did.
 *  ponytail: the colour inside stays the native `TokenPicker` (swatch + select) rather than the
 *  grid — a popover inside a popover buys nothing here, and the select is walkable as-is. */
function ShadowEditor({ node, theme, set }: { node: BlockNode; theme: Theme; set: Setter }): React.JSX.Element {
  const s = node.style?.shadow;
  const colour = s ? theme.colors[s.color] : undefined;

  const patch = (key: 'x' | 'blur' | 'spread', value: number | undefined): void =>
    set('style.shadow', (n) => {
      if (!n.style?.shadow) return;
      if (key === 'blur' && value === undefined) return; // blur is required — an empty box is not a delete
      assignStyle(n, { shadow: { ...n.style.shadow, [key]: value } });
    });

  return (
    <Popover
      label="shadow"
      trigger={
        <>
          <span
            className="size-4 shrink-0 rounded-sm border bg-muted"
            style={s && colour ? { boxShadow: `${s.x ?? 0}px ${s.y}px ${s.blur}px ${s.spread ?? 0}px ${colour.light}` } : undefined}
          />
          <span className="truncate text-[12px]">{s ? `${s.y}/${s.blur}` : 'none'}</span>
        </>
      }
    >
      <div className="space-y-2">
        <Field label="y">
          <NumberInput
            aria-label="shadow y"
            value={s?.y}
            onChange={(y) =>
              set('style.shadow', (n) =>
                assignStyle(n, {
                  shadow:
                    y === undefined
                      ? undefined
                      : {
                          ...n.style?.shadow,
                          y,
                          blur: n.style?.shadow?.blur ?? 8,
                          color: n.style?.shadow?.color ?? firstKey(theme.colors),
                        },
                })
              )
            }
          />
        </Field>
        <Field label="x">
          <NumberInput aria-label="shadow x" value={s?.x} onChange={(x) => patch('x', x)} />
        </Field>
        <Field label="blur">
          <NumberInput aria-label="shadow blur" min={0} value={s?.blur} onChange={(v) => patch('blur', v)} />
        </Field>
        <Field label="spread">
          <NumberInput aria-label="shadow spread" min={0} value={s?.spread} onChange={(v) => patch('spread', v)} />
        </Field>
        <Field label="colour">
          <TokenPicker
            label="shadow colour"
            kind="colors"
            theme={theme}
            value={s?.color}
            onChange={(color) =>
              set('style.shadow', (n) => {
                if (!n.style?.shadow || color === undefined) return;
                assignStyle(n, { shadow: { ...n.style.shadow, color } });
              })
            }
          />
        </Field>
      </div>
    </Popover>
  );
}

/** Same shape, same anchor rule: `width` is what creates a border, so clearing it removes it. */
function BorderEditor({ node, theme, set }: { node: BlockNode; theme: Theme; set: Setter }): React.JSX.Element {
  const b = node.style?.border;
  const colour = b ? theme.colors[b.color] : undefined;

  return (
    <Popover
      label="border"
      trigger={
        <>
          <span
            className="size-4 shrink-0 rounded-sm"
            style={
              b && colour
                ? { border: `${Math.min(b.width, 4)}px solid ${colour.light}` }
                : { border: '1px dashed hsl(var(--border))' }
            }
          />
          <span className="truncate text-[12px]">{b ? `${b.width}` : 'none'}</span>
        </>
      }
    >
      <div className="space-y-2">
        <Field label="width">
          <NumberInput
            aria-label="border width"
            min={0}
            value={b?.width}
            onChange={(width) =>
              set('style.border', (n) =>
                assignStyle(n, {
                  border:
                    width === undefined
                      ? undefined
                      : { width, color: n.style?.border?.color ?? firstKey(theme.colors) },
                })
              )
            }
          />
        </Field>
        <Field label="colour">
          <TokenPicker
            label="border colour"
            kind="colors"
            theme={theme}
            value={b?.color}
            onChange={(color) =>
              set('style.border', (n) => {
                if (!n.style?.border || color === undefined) return;
                assignStyle(n, { border: { ...n.style.border, color } });
              })
            }
          />
        </Field>
      </div>
    </Popover>
  );
}

function assignStyle(node: BlockNode, patch: Partial<NonNullable<BlockNode['style']>>): void {
  const next: Record<string, unknown> = { ...node.style, ...patch };
  for (const [key, value] of Object.entries(next)) if (value === undefined) delete next[key];
  if (Object.keys(next).length === 0) delete node.style;
  else node.style = next as NonNullable<BlockNode['style']>;
}

function setPadding(set: Setter, padding: ReturnType<typeof Object> | undefined): void {
  set('padding', (node) => {
    const target = node as { padding?: unknown };
    if (padding) target.padding = padding;
    else delete target.padding;
  });
}

const firstKey = (record: Record<string, unknown>): string => Object.keys(record)[0] ?? '';
